import { useState, useEffect } from 'react';
import { Image, Film, Music, Search, RefreshCw } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { mediaApi } from '../../api/client';
import AudioThumbnail from '../shared/AudioThumbnail';

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function MediaTypeIcon({ type }) {
  if (type === 'video') return <Film size={12} />;
  if (type === 'audio') return <Music size={12} />;
  return <Image size={12} />;
}

function AssetThumbnail({ asset }) {
  if (asset.type === 'image') {
    return (
      <img
        src={asset.thumbnailUrl || asset.thumbnail || asset.url}
        alt={asset.originalFilename}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  if (asset.type === 'video') {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', background: '#18181b' }}>
        <video
          src={asset.url}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          preload="metadata"
          muted
          playsInline
        />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(0,0,0,0.45)', borderRadius: '50%', padding: 4, display: 'flex' }}>
            <Film size={14} style={{ color: '#fff' }} />
          </div>
        </div>
      </div>
    );
  }
  return <AudioThumbnail filename={asset.originalFilename} size="sm" />;
}

function AssetGrid({ assets, onUse }) {
  const [hovered, setHovered] = useState(null);

  if (assets.length === 0) {
    return (
      <div style={{ padding: '24px 8px', textAlign: 'center' }}>
        <Image size={28} style={{ color: 'var(--t-muted)', margin: '0 auto 8px' }} />
        <p style={{ fontSize: 12, color: 'var(--t-muted)' }}>No media found</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
      {assets.map((asset) => (
        <button
          key={asset.id}
          onClick={() => onUse(asset)}
          onMouseEnter={() => setHovered(asset.id)}
          onMouseLeave={() => setHovered(null)}
          title={`${asset.originalFilename} · ${formatBytes(asset.size)}`}
          style={{
            position: 'relative',
            aspectRatio: '1',
            borderRadius: 6,
            overflow: 'hidden',
            border: `1.5px solid ${hovered === asset.id ? 'var(--brand-primary)' : 'var(--t-border)'}`,
            background: 'var(--t-panel)',
            cursor: 'pointer',
            padding: 0,
            transition: 'border-color 0.15s',
          }}
        >
          <AssetThumbnail asset={asset} />

          {/* Type badge */}
          <div style={{
            position: 'absolute', top: 3, left: 3,
            background: 'rgba(0,0,0,0.6)', borderRadius: 3, padding: '1px 3px',
            display: 'flex', alignItems: 'center', gap: 2, color: '#fff',
          }}>
            <MediaTypeIcon type={asset.type} />
          </div>

          {/* Hover overlay */}
          {hovered === asset.id && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(99,102,241,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#fff',
                background: 'var(--brand-primary)', borderRadius: 4, padding: '2px 6px',
              }}>Use</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

export default function MediaBrowser() {
  const [tab, setTab] = useState('story');
  const [search, setSearch] = useState('');
  const [storyAssets, setStoryAssets] = useState([]);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const story = useEditorStore((s) => s.story);
  const activePageId = useEditorStore((s) => s.activePageId);
  const addLayer = useEditorStore((s) => s.addLayer);

  function normalise(result) {
    const items = result?.items ?? result ?? [];
    return items.map((a) => ({ ...a, id: a.id ?? a._id }));
  }

  async function loadStoryMedia() {
    if (!story) return;
    setLoading(true);
    try {
      const result = await mediaApi.list({ storyId: story.id });
      setStoryAssets(normalise(result));
    } catch {
      setStoryAssets([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadLibraryMedia() {
    setLoading(true);
    try {
      const result = await mediaApi.list({ search: search || undefined });
      setLibraryAssets(normalise(result));
    } catch {
      setLibraryAssets([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (tab === 'story') {
      loadStoryMedia();
    } else {
      loadLibraryMedia();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, story?.id]);

  function handleUse(asset) {
    if (!activePageId || !story) return;
    const isVideo = asset.type === 'video';
    const isAudio = asset.type === 'audio';

    if (isAudio) {
      addLayer(activePageId, {
        type: 'audio',
        mediaId: asset.id,
        src: asset.url,
        autoplay: true,
        loop: true,
        showIcon: true,
      }, { x: 5, y: 5, width: 20, height: 10, rotation: 0, opacity: 1 });
    } else if (isVideo) {
      addLayer(activePageId, {
        type: 'video',
        mediaId: asset.id,
        src: asset.url,
        autoplay: true,
        loop: true,
        muted: true,
      }, { x: 0, y: 0, width: 100, height: 100, rotation: 0, opacity: 1 });
    } else {
      addLayer(activePageId, {
        type: 'image',
        mediaId: asset.id,
        src: asset.url,
        alt: asset.originalFilename,
        objectFit: 'cover',
      }, { x: 0, y: 0, width: 100, height: 100, rotation: 0, opacity: 1 });
    }
  }

  const assets = tab === 'story' ? storyAssets : libraryAssets;
  const filtered = assets.filter((a) => {
    const matchesType = filter === 'all' || a.type === filter;
    const matchesSearch = !search || a.originalFilename.toLowerCase().includes(search.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sub-tab bar */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--t-border)',
        background: 'var(--t-surface)', flexShrink: 0,
      }}>
        {(['story', 'library']).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1, padding: '7px 4px', fontSize: 11, fontWeight: 500,
              color: tab === t ? 'var(--brand-primary)' : 'var(--t-muted)',
              borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: tab === t ? '2px solid var(--brand-primary)' : '2px solid transparent',
              background: 'transparent', cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            {t === 'story' ? 'Story Media' : 'Media Library'}
          </button>
        ))}
      </div>

      {/* Search + filter */}
      <div style={{ padding: '6px 8px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--t-muted)' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && tab === 'library') loadLibraryMedia(); }}
            placeholder="Search files..."
            style={{
              width: '100%', paddingLeft: 26, paddingRight: 8, paddingTop: 5, paddingBottom: 5,
              fontSize: 11, borderRadius: 6, border: '1px solid var(--t-border)',
              background: 'var(--t-panel)', color: 'var(--t-text)', outline: 'none',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['all', 'image', 'video', 'audio']).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                flex: 1, padding: '3px 2px', fontSize: 10, fontWeight: 500,
                borderRadius: 4, cursor: 'pointer',
                background: filter === f ? 'var(--brand-primary)' : 'var(--t-panel)',
                color: filter === f ? '#fff' : 'var(--t-muted)',
                border: `1px solid ${filter === f ? 'var(--brand-primary)' : 'var(--t-border)'}`,
                textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
          <button
            onClick={() => tab === 'story' ? loadStoryMedia() : loadLibraryMedia()}
            title="Refresh"
            style={{
              padding: '3px 6px', borderRadius: 4, cursor: 'pointer',
              background: 'var(--t-panel)', border: '1px solid var(--t-border)',
              color: 'var(--t-muted)', display: 'flex', alignItems: 'center',
            }}
          >
            <RefreshCw size={11} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {/* Asset grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
        {loading ? (
          <div style={{ padding: '24px 8px', textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: 'var(--t-muted)' }}>Loading...</p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 10, color: 'var(--t-muted)', marginBottom: 6 }}>
              {filtered.length} {filtered.length === 1 ? 'file' : 'files'}
              {tab === 'story' ? ' in this story' : ' in library'}
            </div>
            <AssetGrid assets={filtered} onUse={handleUse} />
          </>
        )}
      </div>
    </div>
  );
}
